import { useCallback, useMemo, useRef, useState } from 'react';

import { logger } from '../../core/Log/logger';
import { GeolocationBoundaries } from '../../domains/Geolocation/Geolocation';
import { Region, RegionId } from '../../resources/region/domain/types/region';
import { getRegionBoundariesFromFront } from '../../resources/regionBoundaries/api/front/regionBoundaries';
import { NotFoundResponse } from '../../server/response/NotFoundResponse';
import { PickDomain } from '../utilities/types/PickDomain';

import type { Locale } from '../../core/i18n/Locale';

type UseRegionBoundariesReturn = {
    getById: (regionId: RegionId) => GeolocationBoundaries | null;
    fetch: (
        region: PickDomain<Region, 'id' | 'slug'>,
        locale: Locale,
    ) => Promise<GeolocationBoundaries | null>;
    isLoading: (regionId: RegionId) => boolean;
};

type FetchPromisesByRegionId = Record<RegionId, Promise<GeolocationBoundaries | null>>;

type RegionBoundariesById = Record<RegionId, GeolocationBoundaries | null>;

export function useRegionBoundaries(): UseRegionBoundariesReturn {
    const [loadingIds, setLoadingIds] = useState<Array<RegionId>>([]);
    const [boundariesById, setBoundariesById] = useState<RegionBoundariesById>({});

    const pendingPromisesRef = useRef<FetchPromisesByRegionId>({});
    const boundariesByIdRef = useRef<RegionBoundariesById>({});
    const loadingIdsRef = useRef<Array<RegionId>>([]);

    boundariesByIdRef.current = boundariesById;
    loadingIdsRef.current = loadingIds;

    const startLoading = useCallback((regionId: RegionId) => {
        setLoadingIds((previousLoadingIds) => {
            return [...previousLoadingIds, regionId];
        });
    }, []);

    const finishLoading = useCallback((regionId: RegionId) => {
        setLoadingIds((previousLoadingIds) => {
            return previousLoadingIds.filter((id) => {
                return id !== regionId;
            });
        });
    }, []);

    const addBoundaries = useCallback(
        (regionId: RegionId, boundaries: GeolocationBoundaries | null) => {
            setBoundariesById((previousBoundariesById) => {
                return {
                    ...previousBoundariesById,
                    [regionId]: boundaries,
                };
            });
        },
        [],
    );

    const addRegionWithoutBoundaries = useCallback(
        (regionId: RegionId) => {
            addBoundaries(regionId, null);
        },
        [addBoundaries],
    );

    const getBoundariesById = useCallback(
        (regionId: RegionId): GeolocationBoundaries | null => {
            return boundariesById[regionId] ?? null;
        },
        [boundariesById],
    );

    const isLoading = useCallback(
        (regionId: RegionId): boolean => {
            return loadingIds.includes(regionId);
        },
        [loadingIds],
    );

    const fetchRegionBoundaries = useCallback(
        async (
            region: PickDomain<Region, 'id' | 'slug'>,
            locale: Locale,
        ): Promise<GeolocationBoundaries | null> => {
            const currentBoundaries = boundariesByIdRef.current;
            const regionWithoutBoundaries = currentBoundaries[region.id] === null;
            const alreadyFetched =
                regionWithoutBoundaries || currentBoundaries[region.id] !== undefined;

            if (alreadyFetched) {
                return currentBoundaries[region.id] ?? null;
            }

            const existingPromise = pendingPromisesRef.current[region.id];
            if (existingPromise) {
                return existingPromise;
            }

            startLoading(region.id);

            const promise = (async (): Promise<GeolocationBoundaries | null> => {
                try {
                    const boundaries = await getRegionBoundariesFromFront({
                        locale,
                        regionSlug: region.slug,
                    });

                    addBoundaries(region.id, boundaries);

                    return boundaries;
                } catch (error) {
                    if (error instanceof NotFoundResponse.Error) {
                        addRegionWithoutBoundaries(region.id);
                        return null;
                    }

                    logger.error('Failed to load region boundaries', {
                        locale,
                        regionId: region.id,
                        regionSlug: region.slug,
                        error,
                    });

                    return null;
                } finally {
                    finishLoading(region.id);
                    delete pendingPromisesRef.current[region.id];
                }
            })();

            pendingPromisesRef.current[region.id] = promise;

            return promise;
        },
        [startLoading, finishLoading, addBoundaries, addRegionWithoutBoundaries],
    );

    return useMemo(() => {
        return {
            getById: getBoundariesById,
            fetch: fetchRegionBoundaries,
            isLoading,
        };
    }, [getBoundariesById, fetchRegionBoundaries, isLoading]);
}
